import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  Collapse,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Typography,
  Card,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { CloudUpload, Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { setAuth } from "../../../app-store/authSlice";
import { useDispatch } from "react-redux";

const SignUp = () => {
  const [payload, setPayload] = useState({
    username: "",
    email: "",
    password: "",
    age: "",
    degree: "",
    experience: "",
    location: "",
    image: null,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleChange = (name) => (e) => {
    const value = name === "image" ? e.target.files[0] : e.target.value;

    setPayload({
      ...payload,
      [name]: value,
    });
  };

  // Prevent mouse wheel from changing number inputs.
  const handleWheel = (e) => {
    e.preventDefault();
    e.target.blur();
  };

  const handleNextStep = () => {
    if (currentStep < 2) {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  async function handleSubmit(e) {
    try {
      e.preventDefault();
      setLoading(true);

      const formData = new FormData();
      formData.append("username", payload.username);
      formData.append("email", payload.email);
      formData.append("password", payload.password);
      formData.append("age", payload.age);
      formData.append("degree", payload.degree);
      formData.append("experience", payload.experience);
      formData.append("location", payload.location);
      formData.append("image", payload.image);

      const { data, status } = await axios.post(
        "https://le-travaille-server.onrender.com/auth/signup",
        formData,
      );

      if (status === 201) {
        dispatch(setAuth({ token: data.token }));

        toast.success(
          "Welcome onboard! You will be redirected to the login screen in a moment.",
        );
        navigate("/auth/login");
      } else {
        toast.error(error.response.data.message);
      }
    } catch (err) {
      console.log(err);
      setError(err.response.data.message);
    } finally {
      setLoading(false);
    }
  }

  // Check if all fields for the current step are filled.
  const isComplete = () => {
    if (currentStep === 0) {
      return !(payload.username && payload.email && payload.password);
    }

    if (currentStep === 1) {
      return !(payload.age && payload.degree && payload.experience);
    }

    if (currentStep === 2) {
      return !(payload.location && payload.image);
    }

    return false;
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        px: { xs: 0.5, sm: 2 },
        py: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 1100,
          width: "100%",
          minHeight: 550,
          borderRadius: {
            xs: 2,
            md: 4,
          },
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* Left Column - Image */}
        <Box
          sx={{
            flex: 1.1,
            minHeight: isMobile ? 250 : "auto",
            backgroundImage: `linear-gradient(
              135deg,
              rgba(0, 120, 111, 0.85),
              rgba(0, 120, 111, 0.95)
            ),
            url("https://i.ibb.co/mh7GRRf/Man-Searching-for-a-Job-removebg.png")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: {
              xs: 3,
              md: 5,
            },
            color: "white",
            position: "relative",
          }}
        >
          {/* Decorative elements */}
          <Box
            sx={{
              position: "absolute",
              top: 30,
              right: 30,
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 215, 0, 0.08)",
              border: "2px solid rgba(255, 215, 0, 0.15)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 50,
              left: 40,
              width: 50,
              height: 50,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 215, 0, 0.06)",
              border: "2px solid rgba(255, 215, 0, 0.1)",
            }}
          />

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              color: "#FFD700",
              mb: 2,
              zIndex: 1,
              textAlign: "center",
              fontSize: {
                xs: "2.5rem",
                md: "3.5rem",
              },
            }}
          >
            Le-Travaille
          </Typography>

          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              textAlign: "center",
              zIndex: 1,
              maxWidth: 350,
              fontWeight: 400,
              fontSize: {
                xs: "1rem",
                md: "1.25rem",
              },
            }}
          >
            Create your account and start your journey
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 4,
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#FFD700",
                opacity: 0.6,
              }}
            />

            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#FFD700",
                opacity: 0.3,
              }}
            />

            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#FFD700",
                opacity: 0.15,
              }}
            />
          </Box>
        </Box>

        {/* Right Column - Form */}
        <Box
          sx={{
            flex: 1.4,
            padding: {
              xs: 2,
              sm: 3,
              md: 5,
            },
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 450,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#00786F",
              textAlign: "center",
              mb: 1,
              fontSize: {
                xs: "1.75rem",
                md: "2.25rem",
              },
            }}
          >
            Create Account
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#666",
              textAlign: "center",
              mb: 3,
              fontSize: "1rem",
            }}
          >
            Already have an account?{" "}
            <span
              onClick={() => (window.location.href = "/auth/login")}
              style={{
                color: "#00786F",
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Login
            </span>
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Collapse in={Boolean(error)}>
                <Alert
                  severity="error"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setError("")}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                  }}
                >
                  {error}
                </Alert>
              </Collapse>
            )}

            <Stepper
              activeStep={currentStep}
              alternativeLabel
              sx={{
                mb: 3,
              }}
            >
              {["Account Info", "Personal Info", "Location & Image"].map(
                (label) => (
                  <Step key={label}>
                    <StepLabel
                      sx={{
                        "& .MuiStepLabel-label": {
                          color: "#1A2332 !important",
                          fontSize: "0.75rem",
                        },

                        "& .Mui-active .MuiStepLabel-label": {
                          color: "#00786F !important",
                          fontWeight: 600,
                        },

                        "& .Mui-completed .MuiStepLabel-label": {
                          color: "#00786F !important",
                        },

                        "& .Mui-completed .MuiStepIcon-root": {
                          color: "#00786F",
                        },

                        "& .MuiStepIcon-root.Mui-active": {
                          color: "#00786F",
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ),
              )}
            </Stepper>

            {/* Step 1 */}
            {currentStep === 0 && (
              <motion.div
                initial={{
                  opacity: 0,
                  x: -100,
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                }}
                transition={{
                  duration: 0.5,
                }}
              >
                <TextField
                  label="Username"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={payload.username}
                  onChange={handleChange("username")}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",

                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },

                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },

                      "&:active": {
                        transform: "scale(0.98)",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                    },

                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,

                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },

                    mb: 2,
                  }}
                />

                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={payload.email}
                  onChange={handleChange("email")}
                  type="email"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",

                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },

                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },

                      "&:active": {
                        transform: "scale(0.98)",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                    },

                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,

                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },

                    mb: 2,
                  }}
                />

                <TextField
                  label="Password"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={payload.password}
                  onChange={handleChange("password")}
                  type={showPassword ? "text" : "password"}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(0, 120, 111, 0.04)",
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",

                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },

                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },

                      "&:active": {
                        transform: "scale(0.98)",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                    },

                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,

                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },

                    mb: 2,
                  }}
                />
              </motion.div>
            )}

            {/* Step 2 */}
            {currentStep === 1 && (
              <motion.div
                initial={{
                  opacity: 0,
                  y: 50,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.5,
                }}
              >
                <TextField
                  label="Age"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={payload.age}
                  onChange={handleChange("age")}
                  type="number"
                  required
                  onWheel={handleWheel}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",

                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },

                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },

                      "&:active": {
                        transform: "scale(0.98)",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                        {
                          WebkitAppearance: "none",
                          margin: 0,
                        },

                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                    },

                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,

                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },

                    mb: 2,
                  }}
                />

                <TextField
                  label="Degree"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={payload.degree}
                  onChange={handleChange("degree")}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",

                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },

                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },

                      "&:active": {
                        transform: "scale(0.98)",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                    },

                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,

                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },

                    mb: 2,
                  }}
                />

                <TextField
                  label="Experience (years)"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={payload.experience}
                  onChange={handleChange("experience")}
                  type="number"
                  required
                  onWheel={handleWheel}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",

                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },

                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },

                      "&:active": {
                        transform: "scale(0.98)",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                        {
                          WebkitAppearance: "none",
                          margin: 0,
                        },

                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                    },

                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,

                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },

                    mb: 2,
                  }}
                />
              </motion.div>
            )}

            {/* Step 3 */}
            {currentStep === 2 && (
              <motion.div
                initial={{
                  opacity: 0,
                  y: 50,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  duration: 0.5,
                }}
              >
                <TextField
                  label="Location"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={payload.location}
                  onChange={handleChange("location")}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      height: "56px",
                      backgroundColor: "#f8f9fa",
                      transition: "all 0.2s ease-in-out",

                      "&:hover": {
                        backgroundColor: "#f1f3f5",
                        transform: "scale(1.01)",
                        boxShadow: "0 2px 8px rgba(0, 120, 111, 0.08)",
                      },

                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                        transform: "scale(0.99)",
                        boxShadow: "0 4px 16px rgba(0, 120, 111, 0.15)",
                      },

                      "&:active": {
                        transform: "scale(0.98)",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00786F",
                        borderWidth: 2,
                      },
                    },

                    "& .MuiInputLabel-root": {
                      fontSize: "0.95rem",
                      color: "#1A2332",
                      fontWeight: 500,

                      "&.Mui-focused": {
                        color: "#00786F",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      padding: "16px 14px",
                    },

                    mb: 2,
                  }}
                />
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUpload />}
                    sx={{
                      backgroundColor: "#00786F",
                      color: "#fff",
                      fontWeight: 600,
                      textTransform: "none",
                      py: 1.5,
                      borderRadius: 2,

                      "&:hover": {
                        backgroundColor: "#005b54",
                        transform: "scale(1.02)",
                      },

                      transition: "transform 0.2s ease-in-out",
                    }}
                    fullWidth
                  >
                    Upload Image
                    <input
                      type="file"
                      accept=".jpg, .png, .jpeg"
                      onChange={handleChange("image")}
                      style={{
                        display: "none",
                      }}
                    />
                  </Button>

                  {payload.image && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        color: "#00786F",
                        fontStyle: "italic",
                        textAlign: "center",
                      }}
                    >
                      Selected: {payload.image.name}
                    </Typography>
                  )}
                </Box>
              </motion.div>
            )}

            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: "space-between",
              }}
            >
              <Grid size={5}>
                {currentStep !== 0 && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handlePrevStep}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1.5,
                      borderColor: "#00786F",
                      color: "#00786F",

                      "&:hover": {
                        borderColor: "#005b54",
                        backgroundColor: "rgba(0, 120, 111, 0.04)",
                      },
                    }}
                  >
                    Previous
                  </Button>
                )}
              </Grid>

              <Grid size={5}>
                {currentStep === 2 ? (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isComplete() || loading}
                    sx={{
                      backgroundColor: "#00786F",
                      color: "#fff",
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1rem",

                      "&:hover": {
                        transform: "scale(1.02)",
                      },

                      transition: "transform 0.2s ease-in-out",

                      "&:disabled": {
                        backgroundColor: "rgba(0, 120, 111, 0.5)",
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: "#fff",
                        }}
                      />
                    ) : (
                      "Sign Up"
                    )}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleNextStep}
                    disabled={isComplete()}
                    sx={{
                      backgroundColor: "#00786F",
                      color: "#fff",
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1rem",

                      "&:hover": {
                        transform: "scale(1.02)",
                      },

                      transition: "transform 0.2s ease-in-out",

                      "&:disabled": {
                        backgroundColor: "rgba(0, 120, 111, 0.5)",
                      },
                    }}
                  >
                    Next
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default SignUp;
